"use client"

import { ArrowRight } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"

function StepBadge({ number }: { number: number }) {
  return (
    <div className="w-5 h-5 rounded-full text-[10px] font-medium flex items-center justify-center shrink-0 bg-bg-surface border border-border-default text-fg-secondary group-hover/scoping:bg-neutral-300 group-hover/scoping:border-neutral-300 group-hover/scoping:text-neutral-700 dark:group-hover/scoping:bg-neutral-600 dark:group-hover/scoping:border-neutral-600 dark:group-hover/scoping:text-neutral-200 transition-colors duration-300">
      {number}
    </div>
  )
}

function Tag({ children }: { children: string }) {
  return (
    <span className="px-2 py-0.5 text-[10px] rounded-full bg-bg-surface border border-border-default text-fg-secondary">
      {children}
    </span>
  )
}

export function ResearchScopingDemo() {
  return (
    <div className="w-full">
      <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
      <div
        className="relative"
        style={{
          overflow: "hidden",
          minHeight: "480px",
          ...FADE_MASK_STYLE,
        }}
      >
        <div className="p-6 pt-2 relative" style={{ width: "calc(100% + 200px)", height: "100%" }}>
          <div className="relative" style={{ marginTop: "16px" }}>
            {/* Output panel — in front */}
            <div
              className="group/output relative z-10 bg-bg-default border border-border-default rounded-xl overflow-hidden shadow-lg"
              style={{
                width: "calc(50% + 30px)",
                marginLeft: "calc(50% - 30px)",
              }}
            >
              {/* Header */}
              <div className="px-4 py-2.5 border-b border-border-default flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-teal-500" />
                <span className="text-xs font-medium text-fg-default">Research output</span>
                <span className="text-[10px] text-fg-secondary">Custom dashboard</span>
              </div>

              {/* Mini dashboard mock */}
              <div className="p-4 flex flex-col gap-3">
                {/* Stat row */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Circulating market cap", value: "$218.4B", change: "+24.1%" },
                    { label: "Asset holders", value: "4.8M", change: "+32.6%" },
                    { label: "Transfer volume (30d)", value: "$1.2T", change: "+15.8%" },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="p-2 rounded-lg bg-bg-surface border border-border-default"
                    >
                      <div className="text-[9px] text-fg-secondary">{stat.label}</div>
                      <div className="text-sm font-medium text-fg-default mt-0.5">{stat.value}</div>
                      <div
                        className={`text-[9px] mt-0.5 ${stat.change.startsWith("+") ? "text-teal-500" : "text-red-400"}`}
                      >
                        {stat.change}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Chart mock */}
                <div className="rounded-lg bg-bg-surface border border-border-default p-3">
                  <div className="text-[10px] text-fg-secondary mb-2">RWA market cap by chain</div>
                  <div className="flex items-end gap-1 h-[60px]">
                    {[
                      [38, 6],
                      [39, 7],
                      [40, 8],
                      [41, 9],
                      [42, 10],
                      [43, 11],
                      [44, 12],
                      [45, 14],
                      [46, 15],
                      [47, 17],
                      [48, 18],
                      [49, 20],
                      [50, 22],
                      [51, 24],
                      [52, 26],
                      [53, 28],
                      [54, 30],
                      [55, 32],
                      [55, 34],
                      [56, 36],
                      [56, 38],
                      [57, 40],
                      [57, 42],
                      [58, 44],
                    ].map((pair, i) => (
                      <div key={i} className="flex-1 flex flex-col gap-px">
                        <div
                          className="w-full rounded-xs bg-blue-500/70 group-hover/output:bg-blue-500 transition-colors duration-300"
                          style={{ height: `${pair[0] * 0.85}px` }}
                        />
                        <div
                          className="w-full rounded-xs bg-purple-500/60 group-hover/output:bg-purple-500 transition-colors duration-300"
                          style={{ height: `${pair[1] * 0.85}px` }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 mt-2">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-xs bg-blue-500/70 group-hover/output:bg-blue-500 transition-colors duration-300" />
                      <span className="text-[8px] text-fg-secondary group-hover/output:text-fg-default transition-colors duration-300">
                        Ethereum
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-xs bg-purple-500/60 group-hover/output:bg-purple-500 transition-colors duration-300" />
                      <span className="text-[8px] text-fg-secondary group-hover/output:text-fg-default transition-colors duration-300">
                        Solana
                      </span>
                    </div>
                  </div>
                </div>

                {/* Breakdown table mock */}
                <div className="rounded-lg bg-bg-surface border border-border-default overflow-hidden">
                  <div className="px-3 py-1.5 border-b border-border-default">
                    <span className="text-[10px] text-fg-secondary">Top assets by market cap</span>
                  </div>
                  {[
                    { name: "USDT", value: "$142.8B", chain: "Ethereum" },
                    { name: "USDC", value: "$52.1B", chain: "Ethereum" },
                    { name: "USDC", value: "$12.4B", chain: "Solana" },
                    { name: "BUIDL", value: "$2.8B", chain: "Ethereum" },
                  ].map((row, i) => (
                    <div
                      key={`${row.name}-${row.chain}`}
                      className={`px-3 py-1.5 flex items-center justify-between text-[10px] ${
                        i < 3 ? "border-b border-border-default" : ""
                      }`}
                    >
                      <span className="text-fg-default">{row.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-fg-default font-medium">{row.value}</span>
                        <span className="text-fg-secondary w-16 text-right">{row.chain}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Input panel — behind */}
            <div
              className="group/scoping absolute bg-bg-default border border-border-default rounded-xl overflow-hidden shadow-lg"
              style={{
                width: "50%",
                top: "24px",
                left: "24px",
              }}
            >
              {/* Header */}
              <div className="px-4 py-2.5 border-b border-border-default flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-xs font-medium text-fg-default">Research scope</span>
              </div>

              {/* Steps */}
              <div className="p-4 flex flex-col gap-4">
                {/* Step 1 — completed */}
                <div className="flex gap-3">
                  <StepBadge number={1} />
                  <div className="flex-1">
                    <div className="text-[11px] font-medium text-fg-default">Research question</div>
                    <div className="mt-1.5 p-2.5 rounded-lg bg-bg-surface border border-border-default">
                      <p className="text-[11px] text-fg-default leading-relaxed">
                        Adoption and growth drivers for tokenized assets on Ethereum vs Solana
                        <span
                          className="inline-block w-px h-[13px] bg-fg-default align-middle ml-0.5"
                          style={{ animation: "blink 700ms step-end infinite" }}
                        />
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 2 — completed */}
                <div className="flex gap-3">
                  <StepBadge number={2} />
                  <div className="flex-1">
                    <div className="text-[11px] font-medium text-fg-default">
                      Analytical framework
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      <Tag>Market cap</Tag>
                      <Tag>Asset holders</Tag>
                      <Tag>Transfer volume</Tag>
                      <Tag>Market sectors</Tag>
                      <Tag>Issuer diversity</Tag>
                      <Tag>Net flows</Tag>
                      <Tag>Agentic payments</Tag>
                    </div>
                  </div>
                </div>

                {/* Step 3 — in progress */}
                <div className="flex gap-3">
                  <StepBadge number={3} />
                  <div className="flex-1">
                    <div className="text-[11px] font-medium text-fg-default">Deliverable</div>
                    <div className="mt-1.5 flex flex-col gap-1.5">
                      {[
                        "Private dashboard with Ethereum vs Solana breakdown",
                        "Monthly updates with key developments",
                      ].map((item) => (
                        <div
                          key={item}
                          className="flex items-center gap-2 text-[10px] text-fg-secondary"
                        >
                          <Icon as={ArrowRight} className="w-2.5 h-2.5 shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
